import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import AssetCard from "./AssetCard";
import BillingInputsSection from "./BillingInputsSection";
import ChargesBreakdownSection from "./ChargesBreakdownSection";
import useAuth from "../../../hooks/useAuth";

export default function Step1InformationView({ asset, isHistoricalData, startDate, endDate, detailsExpanded, onMakeChanges, onToggleDetails }) {
  const { user } = useAuth();
  const isInternalStaff = ["admin", "manager", "inventory"].includes(user?.role);
  const storageCharges = asset.storageCharges || {};
  const taxCharges = asset.taxCharges || {};
  const billingCharges = asset.billingCharges || {};

  return (
    <Box display="flex" flexDirection="column" width="100%">
      {/* Invoice Period */}
      {startDate && endDate && (
        <Typography fontSize={15} color="text.secondary" align="center" sx={{ mb: 2 }}>
          Invoice Period: {startDate} - {endDate}
        </Typography>
      )}

      {/* Asset Card */}
      <Box display="flex" justifyContent="center">
        <AssetCard asset={asset} />
      </Box>

      {/* Receipt Section - Always Visible */}
      <Box display="flex" justifyContent="center">
        <ChargesBreakdownSection
          storageCharges={storageCharges}
          taxCharges={taxCharges}
          billingCharges={billingCharges}
        />
      </Box>

      {/* Collapsible Billing Details */}
      <Collapse in={detailsExpanded}>
        <Box mb={4} display="flex" flexDirection="column" alignItems="center" width="100%">
          <Divider sx={{ width: '100%', mb: 2 }} />
          <Typography 
            variant="body1" 
            color="text.secondary" 
            fontWeight={500} 
            align="center"
            sx={{ mb: 2 }}
          >
            Calculation Details
          </Typography>
          <BillingInputsSection 
            asset={asset}
            storageCharges={storageCharges}
            isHistoricalData={isHistoricalData}
          />
        </Box>
      </Collapse>

      {/* Actions - Only show when expanded and user is internal staff */}
      {detailsExpanded && isInternalStaff && (
        <Box display="flex" justifyContent="flex-end" gap={2} mb={3} width="100%">
          <Button 
            variant="outlined"
            color="primary"
            onClick={onMakeChanges}
            sx={{ 
              px: 3,
              py: 0.75,
              fontSize: "14px",
              fontWeight: 400,
              textTransform: "none",
              borderRadius: 2
            }}
          >
            Update Billing Inputs
          </Button>
        </Box>
      )}

      {/* Footer-style expander - Hide when expanded */}
      {!detailsExpanded && (
        <Box 
          width="100%" 
          display="flex" 
          flexDirection="column" 
          alignItems="center"
          sx={{ 
            borderTop: "1px solid",
            borderColor: "grey.200",
            pt: 3,
            mt: 2
          }}
        >
          <Box
            onClick={() => onToggleDetails(!detailsExpanded)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              cursor: "pointer",
              "&:hover": {
                "& .MuiTypography-root": {
                  textDecoration: "underline"
                }
              }
            }}
          >
            <Typography 
              variant="body2" 
              fontSize="16px"
              sx={{ color: "#6B8DC6" }}
            >
              View how this total was calculated
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
